// Login Page
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogIn, Mail, Lock, Check } from 'lucide-react';

// Destino al que volver tras entrar (enlaces del móvil y push). Solo se acepta
// una ruta interna del ERP: empieza por "/" (no por "//"), sin "://", sin
// barras invertidas ni espacios o caracteres de control (el navegador los
// convierte o los quita y "/\otro.com" acabaría en otro dominio), y que no sea
// la raíz ni una pantalla de acceso. Si no vale, devuelve ''.
const PREFIJOS_NO_DESTINO = ['/login', '/empleado/login', '/crear-contrasena'];
export const destinoSeguroTrasLogin = (valor) => {
  if (typeof valor !== 'string') return '';
  if (!valor.startsWith('/') || valor.startsWith('//')) return '';
  if (valor === '/' || valor.includes('://') || valor.includes('\\')) return '';
  for (let i = 0; i < valor.length; i += 1) {
    const codigo = valor.charCodeAt(i);
    if (codigo <= 32 || codigo === 127) return '';
  }
  if (PREFIJOS_NO_DESTINO.some((p) => valor.startsWith(p))) return '';
  return valor;
};

// Destino guardado por el interceptor de api.js al caducar la sesión
// (sessionStorage 'login_desde'). Se lee una vez y se borra.
export const leerYBorrarDestinoGuardado = () => {
  try {
    const valor = sessionStorage.getItem('login_desde') || '';
    sessionStorage.removeItem('login_desde');
    return valor;
  } catch (err) {
    return '';
  }
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberEmail, setRememberEmail] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const desdeNavegacion = destinoSeguroTrasLogin(location.state?.desde);

  // Para volver al destino tras entrar (enlaces del móvil y push): se copia
  // también a sessionStorage porque una contraseña mal puesta da 401 y api.js
  // recarga /login, y con la recarga se pierde el estado de la navegación
  useEffect(() => {
    if (!desdeNavegacion) return;
    try {
      sessionStorage.setItem('login_desde', desdeNavegacion);
    } catch (err) {
      // sin sessionStorage queda solo el estado de la navegación
    }
  }, [desdeNavegacion]);

  // Cargar email recordado al montar el componente
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberEmail(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Guardar o eliminar email según preferencia
    if (rememberEmail) {
      localStorage.setItem('rememberedEmail', email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }

    const result = await login(email, password);

    if (result.success) {
      // Para volver al destino tras entrar (enlaces del móvil y push): el de la
      // navegación (App.js) o el que guardó api.js; el guardado se borra siempre
      const guardado = leerYBorrarDestinoGuardado();
      const desde = desdeNavegacion || destinoSeguroTrasLogin(guardado);
      // Sin destino: dirección entra en Finanzas (la más usada); el resto pasa por la raíz,
      // que decide entre Mi Inicio (hasta fichar) y el calendario (InicioSegunFichaje)
      let rol = '';
      try { rol = JSON.parse(localStorage.getItem('user') || '{}')?.rol || ''; } catch (err) { rol = ''; }
      if (desde) {
        navigate(desde, { replace: true });
      } else if (rol === 'direccion') {
        navigate('/finanzas');
      } else {
        navigate('/', { replace: true });
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-duetto-primary via-duetto-secondary to-teal-400 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo Card */}
        <div className="bg-white rounded-2xl shadow-xl p-4 mb-4 sm:p-8 sm:mb-8">
          <div className="flex flex-col items-center">
            <img 
              src="/duetto-logo-negro.png" 
              alt="Duetto Catering" 
              className="h-14 sm:h-24 object-contain"
            />
          </div>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Iniciar Sesión</h2>
            <p className="text-gray-600">Accede a tu cuenta</p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-login-form="true">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                  placeholder="tu@email.com"
                  autoComplete="username"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            {/* Recordar email: casilla y texto en un solo botón (más fácil de pulsar) */}
            <button
              type="button"
              role="checkbox"
              aria-checked={rememberEmail}
              onClick={() => setRememberEmail(!rememberEmail)}
              className="flex items-center gap-2 py-2 text-left text-sm text-gray-600 cursor-pointer"
            >
              <span
                className={`flex items-center justify-center w-5 h-5 shrink-0 rounded border ${
                  rememberEmail
                    ? 'bg-duetto-primary border-duetto-primary'
                    : 'border-gray-300 bg-white'
                } transition-colors`}
              >
                {rememberEmail && <Check className="w-3 h-3 text-white" />}
              </span>
              Recordar mi email en este dispositivo
            </button>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-duetto-primary text-white py-3 rounded-lg hover:bg-duetto-secondary transition-colors disabled:opacity-50 font-medium"
            >
              {loading ? (
                <span>Iniciando sesión...</span>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>Iniciar Sesión</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-400 text-center">
              ¿Olvidaste tu contraseña? Contacta con el administrador.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
